import {WsServerConnection, ConnectionState} from '@stores/serverConnection/wsServerConnection';
import {PM_ACTIONS} from '@stores/serverConnection/types';
import {getLocalDoc, saveLocalDoc, removeLocalDoc} from '@stores/document/DocumentLocal';
import {IAuthService} from '../../services/auth/AuthService';

const BLANK_DOC = {type: 'doc', content: [{type: 'paragraph'}]};
const NOW_ISO = () => new Date().toISOString();

// Tracks per-doc collab version so SEND_EVENTS acks are monotonically increasing.
const docVersions = new Map<string, number>();

function nextDocVersion(docId: string): number {
    const v = (docVersions.get(docId) ?? 0) + 1;
    docVersions.set(docId, v);
    return v;
}

/**
 * A fully local WsServerConnection for mock/demo mode.
 *
 * Never opens a WebSocket. Overrides sendMsg() to intercept PM_ACTIONS and
 * immediately dispatch fake responses via the inherited _dispatchToHandlers().
 * Uses microtask timing (Promise.resolve().then) so requestLockMap entries
 * are registered before the response fires.
 *
 * Document state is persisted to localStorage under lg_d_{docId} keys —
 * the same layer the real app already uses for offline-first caching.
 */
export class MockWsServerConnection extends WsServerConnection {
    constructor(authService: IAuthService) {
        super(authService);
    }

    override connect(): void {
        // Skip WebSocket creation — immediately clear the WAITING_SERVER barrier
        // which triggers the auth handshake flow and flushes the message queue.
        this.clearWaitFor(ConnectionState.WAITING_SERVER);
    }

    override watchNetworkState(_getIsOnline: () => boolean): void {
        // Mock is always online — never block on network state.
    }

    override sendMsg = (msg: any): boolean => {
        const action = msg.a;
        if (!action) {
            // Keepalive ping (p: 'ka') or other non-action messages — ignore.
            return true;
        }

        // Microtask: ensures requestLockMap.set() runs before we dispatch the
        // response, so the promise registered in httpLikeRequestTroughWebsocket
        // is already in the map when the handler iterates it.
        Promise.resolve().then(() => {
            const response = this._handleMockAction(action, msg);
            if (response !== null) {
                this._dispatchToHandlers(response);
            }
        });

        return true;
    };

    private _handleMockAction(action: string, msg: any): any | null {
        switch (action) {
            // ── Auth ─────────────────────────────────────────────────────────
            case PM_ACTIONS.AUTH:
                return {
                    a: PM_ACTIONS.AUTH,
                    exp: Math.floor(Date.now() / 1000) + 86400 * 365,
                };

            // ── Document lifecycle ────────────────────────────────────────────
            case PM_ACTIONS.NEW_DOC: {
                const content = msg.content || BLANK_DOC;
                const existing = getLocalDoc(msg.itemId);
                if (!existing) {
                    saveLocalDoc(msg.itemId, {
                        docId: msg.itemId,
                        doc: content,
                        version: 0,
                        latestEditor: '',
                        updatedAt: NOW_ISO(),
                        comments: {version: 0, comments: []},
                        commentVersion: 0,
                    });
                }
                return {a: PM_ACTIONS.NEW_DOC, itemId: msg.itemId, s: 200};
            }

            case PM_ACTIONS.COLLAB_GET_DOCUMENT: {
                const stored = getLocalDoc(msg.docId);
                const doc = stored?.doc ?? BLANK_DOC;
                const version = stored?.version ?? 0;
                return {
                    a: PM_ACTIONS.COLLAB_GET_DOCUMENT,
                    docId: msg.docId,
                    doc,
                    version,
                    latestEditor: stored?.latestEditor ?? '',
                    updatedAt: stored?.updatedAt ?? NOW_ISO(),
                    comments: stored?.comments ?? {version: 0, comments: []},
                    commentVersion: stored?.commentVersion ?? 0,
                    s: 200,
                };
            }

            case PM_ACTIONS.COLLAB_SEND_EVENTS: {
                // Ack the commit. We don't apply steps locally — the editor's
                // collab plugin tracks them in-memory and in the persisted
                // localStorage collab state.
                const v = nextDocVersion(msg.docId);
                return {a: PM_ACTIONS.COLLAB_SEND_EVENTS, docId: msg.docId, serverVersion: v, s: 200};
            }

            case PM_ACTIONS.COLLAB_GET_EVENTS:
                return {
                    a: PM_ACTIONS.COLLAB_GET_EVENTS,
                    docId: msg.docId,
                    serverVersion: docVersions.get(msg.docId) ?? 0,
                    commits: [],
                    comments: {version: 0, comments: []},
                    s: 200,
                };

            case PM_ACTIONS.COLLAB_SYNC:
            case PM_ACTIONS.COLLAB_SYNC_SELECTION:
                return null; // fire-and-forget, no response expected

            // ── Document state mutations ──────────────────────────────────────
            case PM_ACTIONS.ARCHIVE_DOC:
                return {a: PM_ACTIONS.ARCHIVE_DOC, docId: msg.docId, s: 200};

            case PM_ACTIONS.TRASH_DOC:
                return {a: PM_ACTIONS.TRASH_DOC, docId: msg.docId, s: 200};

            case PM_ACTIONS.DELETE_FOREVER:
                if (msg.docId) removeLocalDoc(msg.docId);
                return {a: PM_ACTIONS.DELETE_FOREVER, docId: msg.docId, s: 200};

            // ── Snapshots (no-op in demo) ─────────────────────────────────────
            case PM_ACTIONS.SNAPSHOT_LIST:
                return {a: PM_ACTIONS.SNAPSHOT_LIST, d: msg.d, items: [], nextToken: null, s: 200};

            case PM_ACTIONS.SNAPSHOT_GET:
                return {a: PM_ACTIONS.SNAPSHOT_GET, d: msg.d, t: msg.t, s: 404};

            case PM_ACTIONS.SNAPSHOT_RESTORE:
                return {a: PM_ACTIONS.SNAPSHOT_RESTORE, d: msg.d, t: msg.t, s: 200};

            case PM_ACTIONS.SNAPSHOT_FORCE_SAVE:
                return {a: PM_ACTIONS.SNAPSHOT_FORCE_SAVE, d: msg.d, s: 200};

            case PM_ACTIONS.SNAPSHOT_SAVE_AS:
                return {a: PM_ACTIONS.SNAPSHOT_SAVE_AS, d: msg.d, s: 200};

            // ── Activity (no collab in single-user demo) ──────────────────────
            case PM_ACTIONS.ACTIVITY_SYNC:
            case PM_ACTIONS.ACTIVITY_PARTIAL:
                return null;

            // ── Workspace (handled via GQL, not WS) ──────────────────────────
            case PM_ACTIONS.WORKSPACE_DEL:
                return {a: PM_ACTIONS.WORKSPACE_DEL, s: 200};

            // ── Lists / location (stub) ───────────────────────────────────────
            case PM_ACTIONS.LISTS_GET:
                return {a: PM_ACTIONS.LISTS_GET, items: [], s: 200};

            case PM_ACTIONS.LOCATION_COUNT:
                return {a: PM_ACTIONS.LOCATION_COUNT, count: 0, s: 200};

            // ── Payments / AI (stub) ─────────────────────────────────────────
            case PM_ACTIONS.PAYMENT_GATEWAY:
            case PM_ACTIONS.PAYMENT_INVOICE:
            case PM_ACTIONS.PAYMENT_INVOICE_UPGRADE:
            case PM_ACTIONS.PAYMENT_INVOICE_DOWNLOAD:
            case PM_ACTIONS.PAYMENT_SUBSCRIPTION_PREVIEW:
            case PM_ACTIONS.PAYMENT_SUBSCRIPTION_UPDATE:
            case PM_ACTIONS.PAYMENT_METHOD_DETAILS:
            case PM_ACTIONS.PAYMENT_METHOD_UPDATE_TRANSACTION:
            case PM_ACTIONS.PAYMENT_METHOD_DELETE:
            case PM_ACTIONS.AI_QUIZ_CREATE:
            case PM_ACTIONS.AI_QUIZ_USAGE:
                return {a: action, s: 503, error: 'Not available in demo mode'};

            default:
                return null;
        }
    }
}
